/**
 * Undo Helpers
 *
 * Utility functions for creating and executing undo operations for bulk task actions.
 */

import type { Task } from '@/types';
import { useAppStore } from '@/store/appStore';
import { toast } from './toast';
import { bulkUpdateTasks, restoreTask } from '@/db';
import { handleError } from '@/utils/errorHandler';

// Data structure for bulk update undo
interface BulkUpdateUndoData {
  taskUpdates: Array<{
    id: string;
    previousValues: Partial<Task>;
  }>;
}

// Data structure for bulk delete undo
interface BulkDeleteUndoData {
  deletedTasks: Task[];
}

/**
 * Helper function to restore a deleted task with its original ID.
 * Delegates to the db layer which handles conversion and event logging.
 */
async function restoreDeletedTask(task: Task): Promise<void> {
  await restoreTask(task);
}

/**
 * Creates an undo action for bulk status or priority changes.
 * Captures the previous values before the update is applied.
 *
 * @param taskIds - Array of task IDs to update
 * @param updates - The updates to apply
 * @param tasks - Current tasks array to capture previous values
 * @param type - Type of undo action ('bulk_status_change' or 'bulk_priority_change')
 * @returns The undo action ID
 */
export function createUndoBulkUpdate(
  taskIds: string[],
  updates: Partial<Omit<Task, 'id'>>,
  tasks: Task[],
  type: 'bulk_status_change' | 'bulk_priority_change'
): string {
  // Capture previous values for all tasks being updated
  const taskUpdates = taskIds
    .map((id) => {
      const task = tasks.find((t) => t.id === id);
      if (!task) return null;

      const previousValues: Partial<Task> = {};
      if (updates.status !== undefined) previousValues.status = task.status;
      if (updates.priority !== undefined) previousValues.priority = task.priority;
      if (updates.tags !== undefined) previousValues.tags = task.tags;

      return {
        id,
        previousValues,
      };
    })
    .filter((update): update is NonNullable<typeof update> => update !== null);

  // Store undo action
  const undoData: BulkUpdateUndoData = { taskUpdates };
  const undoId = useAppStore.getState().storeUndoAction(type, undoData);

  return undoId;
}

/**
 * Creates an undo action for bulk delete operations.
 * Captures the full task objects before deletion.
 *
 * @param taskIds - Array of task IDs to delete
 * @param tasks - Current tasks array to capture full task data
 * @returns The undo action ID
 */
export function createUndoBulkDelete(taskIds: string[], tasks: Task[]): string {
  // Capture full task objects before deletion
  const deletedTasks = taskIds
    .map((id) => {
      const task = tasks.find((t) => t.id === id);
      if (!task) return null;

      // Remove derived fields that are added by useTaskEnrichment
      const { path: _path, spaceName: _spaceName, folderPath: _folderPath, listName: _listName, ...taskData } = task;
      return taskData;
    })
    .filter((task): task is Task => task !== null);

  // Store undo action
  const undoData: BulkDeleteUndoData = { deletedTasks };
  const undoId = useAppStore.getState().storeUndoAction('bulk_delete', undoData);

  return undoId;
}

/**
 * Executes an undo operation based on the stored undo action.
 *
 * @param undoId - The ID of the undo action to execute
 */
export async function executeBulkUndo(undoId: string): Promise<void> {
  const undoAction = useAppStore.getState().getUndoAction(undoId);

  if (!undoAction) {
    toast('Undo action not found', 'error');
    return;
  }

  try {
    switch (undoAction.type) {
      case 'bulk_status_change':
      case 'bulk_priority_change': {
        const data = undoAction.data as BulkUpdateUndoData;

        // Group tasks by their previous values for batching
        const updateGroups = new Map<string, { ids: string[]; previousValues: Partial<Task> }>();

        for (const { id, previousValues } of data.taskUpdates) {
          const key = JSON.stringify(previousValues);
          if (!updateGroups.has(key)) {
            updateGroups.set(key, { ids: [], previousValues });
          }
          updateGroups.get(key)!.ids.push(id);
        }

        // Execute batched updates
        for (const { ids, previousValues } of updateGroups.values()) {
          await bulkUpdateTasks(ids, previousValues);
        }

        const actionType = undoAction.type === 'bulk_status_change' ? 'status change' : 'priority change';
        toast(`Undid ${actionType} for ${data.taskUpdates.length} task(s)`, 'success');
        break;
      }

      case 'bulk_delete': {
        const data = undoAction.data as BulkDeleteUndoData;

        // Restore deleted tasks with their original IDs
        for (const task of data.deletedTasks) {
          await restoreDeletedTask(task);
        }

        toast(`Restored ${data.deletedTasks.length} deleted task(s)`, 'success');
        break;
      }

      default:
        toast('Unknown undo action type', 'error');
        return;
    }

    // Clear undo action after successful execution
    useAppStore.getState().clearUndoAction(undoId);
  } catch (error) {
    handleError(error, { level: 'error', message: 'Errore nell\'annullamento dell\'azione' });
    toast('Failed to undo action', 'error');
  }
}

/**
 * Shows a toast notification with an undo button.
 *
 * @param message - The success message to display
 * @param undoId - The ID of the undo action
 */
export function showUndoToast(message: string, undoId: string): void {
  toast(
    message,
    'success',
    5000,
    {
      label: 'Undo',
      onClick: () => {
        executeBulkUndo(undoId);
      },
    },
    undoId
  );
}
